import React, { useEffect, useState } from 'react';
import {
  IonCard,
  IonCardContent,
  IonCardTitle,
  IonContent,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  IonText,
  IonBadge,
  IonButton,
  IonToggle,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonLabel,
  IonChip,
} from '@ionic/react';
import {
  searchOutline,
  clipboardOutline,
  analyticsOutline,
  cloudUploadOutline,
  informationCircleOutline,
  arrowForwardOutline,
  buildOutline,
  layersOutline,
} from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { auth } from '../services/UserAuthService';
import { config } from '@/config/config';
import { useOffline } from '../contexts/OfflineContext';
import PageLayout from '@/components/navigation/PageLayout';
import { usePageTitle, usePageTitleContext } from '@/contexts/PageTitleContext';
import { lookupDataService } from '@/services/data/LookupDataService';
import { featureFlags, PageKey } from '@/config/featureFlags';
import tokenRefreshService from '@/services/TokenRefreshService';
import { dataManager } from '@/services/data/DataManager';
import { databaseCachingService, CachedModuleSummary } from '@/services/offline/DatabaseCachingService';
import permissionService from '@/services/PermissionService';
import { personOutline, businessOutline, peopleOutline, constructOutline } from 'ionicons/icons';
import InspectionCalendarPanel from '@/components/common/InspectionCalendarPanel';
import { apiService } from '@/services/ApiService';
import { offlineModeManager } from '@/services/offline/OfflineModeManager';
import InspectionModal from '@/components/inspection/modal/InspectionModal';
import { InspectionModalData } from '@/types/model/inspection-modal';
import { InspectionScheduleLineItem } from '@/types/response/InspectionScheduleResponses';
import {
  addDays,
  filterByPreferences,
  formatDateParam,
} from '@/services/util/inspectionCalendarUtils';

type FocusTile = 'module-selection' | 'offline-operations';

const Main: React.FC = () => {
  const history = useHistory();
  const { shouldBypassLogin } = useOffline();
  const { setContractName } = usePageTitleContext();
  usePageTitle('Main');

  const [isOfflineModeEnabled, setIsOfflineModeEnabled] = useState(
    () => offlineModeManager.getCurrentStatus().isOfflineMode
  );

  useEffect(() => {
    return offlineModeManager.addListener((status) => {
      setIsOfflineModeEnabled(status.isOfflineMode);
    });
  }, []);

  // Offline Management: platform flag must allow it
  const showOfflineManagement = featureFlags.isOfflineManagementEnabled();
  
  const [moduleSummaries, setModuleSummaries] = useState<CachedModuleSummary[]>([]);

  const hasPendingOfflineData = moduleSummaries.some(m => m.pendingSync > 0);
  const totalPendingCount = moduleSummaries.reduce((acc, m) => acc + m.pendingSync, 0);
  const [contracts, setContracts] = useState<{ Id: number; Name: string }[]>([]);
  const [selectedContractId, setSelectedContractId] = useState<number | null>(null);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [lookupReady, setLookupReady] = useState(false);
  const [inspectionScheduleItems, setInspectionScheduleItems] = useState<InspectionScheduleLineItem[]>([]);
  const [inspectionScheduleLoading, setInspectionScheduleLoading] = useState(false);
  const [inspectionScheduleError, setInspectionScheduleError] = useState<string | null>(null);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [selectedTile, setSelectedTile] = useState<FocusTile>('module-selection');
  const [isInspectionModalOpen, setIsInspectionModalOpen] = useState(false);
  const [inspectionModalData, setInspectionModalData] = useState<InspectionModalData | null>(null);

  const isMobile = viewportWidth <= 768;
  const isDesktopWide = viewportWidth >= 992;

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Check if user is logged in (skip if offline mode bypasses login)
    if (!shouldBypassLogin) {
      const token = auth.getToken();
      if (!token) {
        history.push('/login');
        return;
      }
    }
  }, [history, shouldBypassLogin]);

  const navigateTo = (url: string) => {
    history.push(url);
  };

  const refreshModuleSummaries = async (): Promise<void> => {
    const summaries = await databaseCachingService.getCachedModuleSummaries();
    setModuleSummaries(summaries);
  };

  useEffect(() => {
    return databaseCachingService.addCacheChangeListener(() => {
      refreshModuleSummaries();
    });
  }, []);

  useEffect(() => {
    refreshModuleSummaries();
    tokenRefreshService.start();

    setContractsLoading(true);
    dataManager.getContracts()
      .then((data: { Id: number; Name: string }[]) => setContracts(data))
      .catch((err: unknown) => console.error('Failed to load contracts:', err))
      .finally(() => setContractsLoading(false));

    return () => tokenRefreshService.stop();
  }, []);

  //listen for contract set. get contractId from user info and set selected contract accordingly
  useEffect(() => {
    const userInfo = auth.getUserDetails();
    if (userInfo && userInfo.contractId) {
      const userConract = contracts.find(c => c.Id === userInfo.contractId);
      if (userConract) {
        handleContractSelect(userConract.Id, userConract.Name);
      }
    }
  }, [contracts]);

  useEffect(() => {
    const reloadData = async () => {
      if (!selectedContractId) {
        setLookupReady(false);
        return;
      }

      setLookupReady(false);

      try {
        await lookupDataService.reload();
        await refreshModuleSummaries();
        setLookupReady(lookupDataService.isLoaded());
      } catch (error) {
        console.error('Failed to initialize lookup data for selected contract:', error);
        setLookupReady(false);
      }
    };

    reloadData();
  }, [selectedContractId]);

  useEffect(() => {
    const loadInspectionSchedule = async () => {
      if (!selectedContractId || !lookupReady) {
        setInspectionScheduleItems([]);
        setInspectionScheduleError(null);
        setInspectionScheduleLoading(false);
        return;
      }

      const selectedDate = new Date();
      const DAYS_BEFORE = config.Inspecting.pastDaysToShow; 
      const DAYS_AFTER = config.Inspecting.futureDaysToShow;
      const from = formatDateParam(addDays(selectedDate, -DAYS_BEFORE));
      const to = formatDateParam(addDays(selectedDate, DAYS_AFTER));

      setInspectionScheduleLoading(true);
      setInspectionScheduleError(null);

      try {
        const rawItems = await apiService.getInspectionScheduleLineItems(from, to);
        const prefs = lookupDataService.getUserPreferences();
        const filteredItems = prefs ? filterByPreferences(rawItems, prefs) : rawItems;
        setInspectionScheduleItems(filteredItems);
      } catch (error) {
        console.error('Failed to load inspection schedule:', error);
        setInspectionScheduleItems([]);
        setInspectionScheduleError('Failed to load inspection schedule.');
      } finally {
        setInspectionScheduleLoading(false);
      }
    };

    void loadInspectionSchedule();
  }, [selectedContractId, lookupReady]);

  const handleContractSelect = async (contractId: number, contractName: string) => {
    auth.setCurrentContract(contractId, contractName);
    setSelectedContractId(contractId);
    setContractName(contractName);
  };

  const handleOpenInspectionModal = (data: InspectionModalData) => {
    setInspectionModalData(data);
    setIsInspectionModalOpen(true);
  };

  const canRenderMainContent = !contractsLoading && lookupReady;

  const getAdminCards = () => {
    const isEnterpriseAdmin = permissionService.isEnterpriseAdmin();
    const isModuleAdmin = permissionService.isModuleAdmin();

    const allAdminCards = [
      {
        id: 'user-preferences',
        url: '/user-preferences',
        icon: personOutline,
        title: 'User Preferences',
        description: 'Email, password, and default settings',
        show: featureFlags.isPageEnabled('userPreferences'),
      },
      {
        id: 'module-settings',
        url: '/module-settings',
        icon: constructOutline,
        title: 'Module Settings',
        description: 'Module details, scheduling, and frequency settings',
        show: featureFlags.isPageEnabled('moduleSettings'),
      },
      {
        id: 'user-management',
        url: '/user-management',
        icon: peopleOutline,
        title: 'User Management',
        description: 'Manage users, privileges, and assignments',
        show: featureFlags.isPageEnabled('userManagement') && (isEnterpriseAdmin || isModuleAdmin),
      },
      {
        id: 'enterprise-management',
        url: '/enterprise-management',
        icon: businessOutline,
        title: 'Enterprise Management',
        description: 'License, password policies, and enterprise settings',
        show: featureFlags.isPageEnabled('enterpriseManagement') && isEnterpriseAdmin,
      },
    ];

    return allAdminCards.filter(c => c.show);
  };

  // Filter cards based on feature flags and offline mode
  const getActionCards = () => {
    if (isOfflineModeEnabled) {
      return [{
        id: 'inspecting',
        url: '/inspecting',
        icon: searchOutline,
        title: 'Inspecting',
        description: 'Perform inspections and assessments',
      }];
    }

    const allCards = [
      {
        id: 'inspecting',
        pageKey: 'inspecting' as PageKey,
        url: '/inspecting',
        icon: searchOutline,
        title: 'Inspecting',
        description: 'Perform inspections and assessments'
      },
      {
        id: 'corrective-actions',
        pageKey: 'correctiveActions' as PageKey,
        url: '/corrective-actions',
        icon: buildOutline,
        title: 'Corrective Actions',
        description: 'Review and manage corrective actions'
      },
      {
        id: 'reporting',
        pageKey: 'reporting' as PageKey,
        url: '/reporting',
        icon: analyticsOutline,
        title: 'Reporting',
        description: 'Generate and view reports'
      },
      {
        id: 'planning',
        pageKey: 'planning' as PageKey,
        url: '/planning',
        icon: clipboardOutline,
        title: 'Planning',
        description: 'Plan and schedule inspections'
      }
    ];

    return allCards.filter(card => {
      if (!featureFlags.isPageEnabled(card.pageKey)) return false;
      if (card.id === 'reporting') return featureFlags.isReportingEnabled();
      return true;
    });
  };

  return (
    <PageLayout>

      <IonContent fullscreen className="ion-padding">
        <div
          style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: isDesktopWide ? '40px' : '20px',
          }}
        >
          <div
            style={isMobile
              ? { display: 'flex', flexDirection: 'column', gap: '16px' }
              : { display: 'flex', gap: '24px', alignItems: 'flex-start' }}
          >

            {/* ── LEFT COLUMN ─────────────────────────────────────── */}
            {!isMobile && (
            <div 
              style={{ 
                flex: 2, 
                minWidth: 0, 
                borderRight: '1px solid var(--ion-color-light-shade)', 
                padding: '0px 24px',
              }}>

              {/* Quick Actions & Admin — only when a module is selected */}
              {canRenderMainContent && selectedContractId !== null && (
                <>
                  <div style={{ marginBottom: '4rem' }}>
                    <h2 style={{ marginBottom: '1rem', color: 'var(--seesor-light-blue)' }}>Quick Actions</h2>
                    <IonGrid>
                      <IonRow>
                        {getActionCards().map((card) => (
                          <IonCol key={card.id} size="12" sizeMd="6" sizeLg="3">
                            <IonCard
                              button
                              onClick={() => navigateTo(card.url)}
                              style={{
                                height: '100%',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                              }}
                            >
                              <IonCardContent
                                style={{
                                  textAlign: 'center',
                                  padding: isMobile ? '1.5rem 1rem' : '2rem 1rem',
                                }}
                              >
                                <IonIcon
                                  icon={card.icon}
                                  style={{
                                    fontSize: isMobile ? '2.5rem' : '3rem',
                                    color: 'var(--seesor-dark-blue)',
                                    marginBottom: '1rem',
                                  }}
                                />
                                <IonCardTitle>{card.title}</IonCardTitle>
                                <IonText color="medium"><p>{card.description}</p></IonText>
                              </IonCardContent>
                            </IonCard>
                          </IonCol>
                        ))}
                      </IonRow>
                    </IonGrid>
                  </div>

                  {!isOfflineModeEnabled && getAdminCards().length > 0 && (
                    <div>
                      <h2 style={{ marginBottom: '1rem', color: 'var(--seesor-light-blue)' }}>Administrative Tasks</h2>
                      <IonGrid>
                        <IonRow>
                          {getAdminCards().map((card) => (
                            <IonCol key={card.id} size="12" sizeMd="6" sizeLg="3">
                              <IonCard
                                button
                                onClick={() => navigateTo(card.url)}
                                style={{
                                  height: '100%',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                }}
                              >
                                <IonCardContent
                                  style={{
                                    textAlign: 'center',
                                    padding: isMobile ? '1.5rem 1rem' : '2rem 1rem',
                                  }}
                                >
                                  <IonIcon
                                    icon={card.icon}
                                    style={{
                                      fontSize: isMobile ? '2.5rem' : '3rem',
                                      color: 'var(--seesor-dark-blue)',
                                      marginBottom: '1rem',
                                    }}
                                  />
                                  <IonCardTitle>{card.title}</IonCardTitle>
                                  <IonText color="medium"><p>{card.description}</p></IonText>
                                </IonCardContent>
                              </IonCard>
                            </IonCol>
                          ))}
                        </IonRow>
                      </IonGrid>
                    </div>
                  )}
                </>
              )}
            </div>
            )}

            {/* ── RIGHT COLUMN ────────────────────────────────────── */}
            {!isOfflineModeEnabled && canRenderMainContent && (
              <div
                style={{
                  flex: 1,
                  minWidth: '280px',
                  position: isMobile ? 'static' : 'sticky',
                  top: '16px',
                }}
              >
                
              {/* Action Focus Area */}
              <div style={{ marginBottom: '4rem' }}>

                {/* Tile Row */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>

                  {/* Module Selection tile */}
                  <button
                    className="focus-tile"
                    onClick={() => setSelectedTile('module-selection')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 14px',
                      border: `1px solid ${selectedTile === 'module-selection' ? 'var(--seesor-dark-blue)' : 'var(--ion-color-light-shade)'}`,
                      borderRadius: '20px',
                      background: selectedTile === 'module-selection' ? 'var(--ion-color-light-tint)' : 'var(--ion-color-light)',
                      cursor: 'pointer',
                      transition: 'background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: selectedTile === 'module-selection' ? 'var(--seesor-dark-blue)' : 'var(--ion-color-dark)',
                      boxShadow: selectedTile === 'module-selection' ? '0 2px 8px rgba(var(--ion-color-primary-rgb), 0.35)' : 'none',
                    }}
                  >
                    <IonIcon
                      icon={layersOutline}
                      style={{ fontSize: '1rem', color: 'var(--seesor-dark-blue)' }}
                    />
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>Select Module</span>
                  </button>

                  {/* Offline Operations tile — feature-gated */}
                  {showOfflineManagement && (
                    <button
                      onClick={() => setSelectedTile('offline-operations')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 14px',
                        border: `1px solid ${selectedTile === 'offline-operations' ? 'var(--seesor-dark-blue)' : 'var(--ion-color-light-shade)'}`,
                        borderRadius: '20px',
                        background: selectedTile === 'offline-operations' ? 'var(--ion-color-light-tint)' : 'var(--ion-color-light)',
                        cursor: 'pointer',
                        transition: 'background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: selectedTile === 'offline-operations' ? 'var(--seesor-dark-blue)' : 'var(--ion-color-dark)',
                        boxShadow: selectedTile === 'offline-operations' ? '0 2px 8px rgba(var(--ion-color-primary-rgb), 0.35)' : 'none',
                      }}
                    >
                      <IonIcon
                        icon={hasPendingOfflineData ? cloudUploadOutline : informationCircleOutline}
                        style={{
                          fontSize: '1rem',
                          color: selectedTile === 'offline-operations' ? 'var(--seesor-dark-blue)' : hasPendingOfflineData ? 'var(--ion-color-warning)' : undefined,
                        }}
                      />
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>Manage Offline</span>
                      {hasPendingOfflineData && (
                        <IonBadge color="warning" style={{ fontSize: '0.65rem', padding: '2px 5px', borderRadius: '8px' }}>
                          {totalPendingCount}
                        </IonBadge>
                      )}
                    </button>
                  )}
                </div>

                {/* Focus Panel */}
                <IonCard style={{ borderRadius: '12px', margin: 0 }}>
                  <IonCardContent style={{ padding: '16px' }}>

                    {selectedTile === 'module-selection' && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <IonText style={{ fontSize: '0.7rem', fontWeight: '600', letterSpacing: '0.08em', color: 'var(--seesor-light-blue)', textTransform: 'uppercase' }}>
                          Module Selection
                        </IonText>
                        
                        {contractsLoading ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <IonSpinner name="crescent" />
                            <IonText color="medium"><span>Loading modules...</span></IonText>
                          </div>
                        ) : (
                          <IonSelect
                            value={selectedContractId}
                            placeholder="Choose a module to continue"
                            onIonChange={(e) => {
                              const selected = contracts.find(c => c.Id === e.detail.value);
                              if (selected) handleContractSelect(selected.Id, selected.Name);
                            }}
                            style={{ 
                              border: '1px solid var(--ion-color-light-shade)', 
                              borderRadius: '8px', 
                              padding: '4px 8px', 
                              fontSize: '0.8rem' 
                            }}
                            interface="popover"
                          >
                            {contracts.map(c => (
                              <IonSelectOption key={c.Id} value={c.Id}>{c.Name}</IonSelectOption>
                            ))}
                          </IonSelect>
                        )}
                      </div>
                    )}

                    {selectedTile === 'offline-operations' && showOfflineManagement && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

                        {/* Header row */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                          <IonText style={{ fontSize: '0.7rem', fontWeight: '600', letterSpacing: '0.08em', color: 'var(--seesor-light-blue)', textTransform: 'uppercase' }}>
                            Offline Data Status
                          </IonText>
                          
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                              <IonText style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#666' }}>Offline Mode</IonText>
                              <IonToggle
                                checked={isOfflineModeEnabled}
                                onIonChange={(e) => offlineModeManager.manualUpdateOfflineMode(e.detail.checked)}
                                style={{ '--background': '#e0e0e0', '--background-checked': '#3880ff', '--handle-background': '#ffffff' }}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Per-module list */}
                        {moduleSummaries.length === 0 ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
                            <IonIcon icon={informationCircleOutline} style={{ color: '#999', fontSize: '18px', flexShrink: 0 }} />
                            <IonText color="medium" style={{ fontSize: '0.875rem' }}>No modules cached for offline use</IonText>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {moduleSummaries.map(m => (
                              <div
                                key={m.contractId}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderRadius: '8px', background: 'var(--ion-color-light)' }}
                              >
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                  <IonText style={{ fontWeight: '600', fontSize: '0.875rem' }}>{m.contractName}</IonText>
                                  <IonText color="medium" style={{ fontSize: '0.75rem' }}>
                                    {m.totalCached} inspection{m.totalCached !== 1 ? 's' : ''} cached
                                  </IonText>
                                </div>
                                {m.pendingSync === 0 ? (
                                  <IonChip color="success" style={{ height: '24px', fontSize: '0.75rem', margin: 0 }}>
                                    <IonLabel>Synced</IonLabel>
                                  </IonChip>
                                ) : (
                                  <IonChip color="warning" style={{ height: '24px', fontSize: '0.75rem', margin: 0 }}>
                                    <IonLabel>{m.pendingSync} pending</IonLabel>
                                  </IonChip>
                                )}

                              <IonButton
                                fill="outline"
                                size="small"
                                onClick={() => navigateTo('/offline-operations')}
                                style={{ '--border-color': hasPendingOfflineData ? '#ffc409' : '#ccc', '--color': hasPendingOfflineData ? '#ffc409' : '#666' }}
                              >
                                <span style={{ marginRight: '8px' }}>Manage Offline Data</span>
                                <IonIcon icon={arrowForwardOutline} />
                              </IonButton>

                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* {!isOfflineModeEnabled && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <IonButton
                              fill="outline"
                              size="small"
                              onClick={() => navigateTo('/offline-operations')}
                              style={{ '--border-color': hasPendingOfflineData ? '#ffc409' : '#ccc', '--color': hasPendingOfflineData ? '#ffc409' : '#666' }}
                            >
                              <span style={{ marginRight: '8px' }}>Manage Offline Data</span>
                              <IonIcon icon={arrowForwardOutline} />
                            </IonButton>
                          </div>
                        </div>
                        )} */}

                      </div>
                    )}

                  </IonCardContent>
                </IonCard>
              </div>

              {/* Inspection Calendar */}
              {canRenderMainContent && (
                <IonCard style={{ borderRadius: '12px', marginBottom: '16px' }}>
                  <IonCardContent style={{ padding: 0 }}>
                    <InspectionCalendarPanel
                      contractId={selectedContractId}
                      inspectionScheduleItems={inspectionScheduleItems}
                      isLoading={inspectionScheduleLoading}
                      error={inspectionScheduleError}
                      onOpenInspection={handleOpenInspectionModal}
                    />
                  </IonCardContent>
                </IonCard>
              )}

              {/* Notifications — placeholder */}
              {/* <IonCard style={{ borderRadius: '12px' }}>
                <IonCardContent style={{ padding: '0.75rem 1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    <IonText style={{ fontSize: '0.7rem', fontWeight: '600', letterSpacing: '0.08em', color: 'var(--ion-color-medium)', textTransform: 'uppercase' }}>
                      Notifications
                    </IonText>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', padding: '2rem 1rem', color: 'var(--ion-color-medium)' }}>
                    <IonIcon icon={notificationsOutline} style={{ fontSize: '2rem' }} />
                    <IonText style={{ fontSize: '0.85rem', textAlign: 'center' }}>
                      Notifications coming soon
                    </IonText>
                  </div>
                </IonCardContent>
              </IonCard> */}

            </div>
            )}
          </div>
        </div>
      </IonContent>

      <InspectionModal
        isOpen={isInspectionModalOpen}
        onDidDismiss={() => {
          setIsInspectionModalOpen(false);
          setInspectionModalData(null);
        }}
        inspectionData={inspectionModalData}
      />
    </PageLayout>
  );
};

export default Main;